"use client";

import { useEffect, useRef, useState } from "react";
import { uploadFileDirect, CreatorVoiceError } from "@/lib/creatorVoice/api";
import { useJob } from "@/lib/creatorVoice/useJob";
import { inputClass, ErrorBox, JobProgress, AudioResult, SharedFreeTrialNote } from "@/components/creatorVoice/CreatorVoiceUI";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import Spinner from "@/components/Spinner";

export default function VoiceCloneTool() {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [sampleText, setSampleText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const trainJob = useJob();

  const [profileId, setProfileId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const generateJob = useJob();

  async function handleTrain() {
    if (!file || !sampleText.trim()) return;
    setUploading(true);
    setUploadError(null);
    try {
      const sampleUrl = await uploadFileDirect(file);
      await trainJob.run("clone_train", { sample_url: sampleUrl, sample_text: sampleText.trim(), name: name.trim() || "My Voice" });
    } catch (e) {
      setUploadError(e instanceof CreatorVoiceError ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    if (trainJob.job?.status === "completed" && !profileId) {
      setProfileId(trainJob.job.result?.voice_profile_id as string);
    }
  }, [trainJob.job, profileId]);

  function reset() {
    setProfileId(null);
    setFile(null);
    setName("");
    setSampleText("");
    trainJob.reset();
    generateJob.reset();
    setText("");
  }

  if (profileId) {
    return (
      <div>
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
          Voice profile ready — "{name || "My Voice"}". Now type something to generate in this voice.
        </p>

        <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-300">Text to speak</label>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Type what you want this cloned voice to say…" className={inputClass} />

        <div className="mt-3 flex justify-between">
          <button type="button" onClick={reset} className="text-sm font-medium text-slate-500 hover:underline dark:text-slate-400">
            ← Clone a different voice
          </button>
          <button
            type="button"
            onClick={() => generateJob.run("clone_generate", { voice_profile_id: profileId, text: text.trim() })}
            disabled={generateJob.submitting || !text.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
          >
            {generateJob.submitting ? <Spinner className="h-4 w-4" /> : null}
            {generateJob.submitting ? "Submitting…" : "Generate"}
          </button>
        </div>

        {generateJob.error ? <ErrorBox message={generateJob.error} /> : null}
        <JobProgress job={generateJob.job} />
        <AudioResult job={generateJob.job} />
      </div>
    );
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Voice sample (WAV, a few seconds, clear audio)</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="mt-2 flex h-24 cursor-pointer items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
      >
        {file ? file.name : "Click to choose a .wav file"}
        <input ref={inputRef} type="file" accept="audio/wav,audio/x-wav" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>

      <label className="mt-3 block text-sm font-medium text-slate-700 dark:text-slate-300">Name this voice (optional)</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Voice" className={inputClass} />

      <label className="mt-3 block text-sm font-medium text-slate-700 dark:text-slate-300">Exactly what was said in the sample</label>
      <textarea
        value={sampleText}
        onChange={(e) => setSampleText(e.target.value)}
        rows={3}
        placeholder="Type the exact words spoken in your uploaded sample — accuracy here improves the clone."
        className={inputClass}
      />

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleTrain}
          disabled={uploading || trainJob.submitting || !file || !sampleText.trim()}
          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          {uploading || trainJob.submitting ? <Spinner className="h-4 w-4" /> : null}
          {uploading ? "Uploading…" : trainJob.submitting ? "Creating Profile…" : "Create Voice Profile"}
        </button>
      </div>
      <SharedFreeTrialNote />

      {uploadError ? <ErrorBox message={uploadError} /> : null}
      {trainJob.error ? <ErrorBox message={trainJob.error} /> : null}
      {trainJob.capped ? (
        <div className="mt-3">
          <UpgradeToPro />
        </div>
      ) : null}
      <JobProgress job={trainJob.job} />
    </div>
  );
}
